import { useEffect } from 'react'
import { usePageStore } from '../libs/hooks/usePageStore'
import { useMutation } from '@tanstack/react-query'
import { useSearchParams } from 'next/navigation'
import { useHttpClient } from '../libs/hooks/useHttpClient'
import { Nullable } from '../utils'

type AuthValidationHandlerProps = {
  charityId: string
  profileId: Nullable<string>
  campaignId: string
}

const AuthValidationHandler = ({ charityId, campaignId, profileId }: AuthValidationHandlerProps) => {
  const searchParams = useSearchParams()
  const httpClient = useHttpClient({ isRelativeUrl: true })
  const { mutateAsync: authenticateProfile, isPending: isAuthenticating } = useMutation({
    mutationKey: ['authenticateProfile'],
    mutationFn: (encodedToken: string) =>
      httpClient<{ profileUrl: string }>('/api/profile/login/authenticate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ charityId, campaignId, encodedToken }),
      }),
    onSuccess: (data) => {
      usePageStore.getState().actions.setHasLoggedIn(true)
      window.location.assign(data.profileUrl)
    },
  })

  const validateToken = async () => {
    const hasLoggedIn = usePageStore.getState().actions.getHasLoggedIn()

    if (!hasLoggedIn) {
      return
    }

    if (!charityId) {
      return
    }

    try {
      const res = await httpClient<{ isLoggedInProfile: boolean }>('/api/profile/login/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          charityId,
          profileId,
        }),
      })

      usePageStore.setState({
        isLoggedIn: true,
        isLoggedInProfile: res.isLoggedInProfile,
      })
    } catch {
      usePageStore.getState().actions.setHasLoggedIn(false)
    }
  }

  useEffect(() => {
    if (isAuthenticating) {
      return
    }

    validateToken()
  }, [charityId, profileId])

  useEffect(() => {
    const token = searchParams?.get('t')

    if (!token) {
      return
    }

    authenticateProfile(token)
  }, [])

  return null
}

export default AuthValidationHandler
